import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import InlineAlert from './InlineAlert.jsx'
import NewListForm from './NewListForm.jsx'
import SheetClose from './SheetClose.jsx'
import { ApiError, api } from '../lib/api.js'
import { useFocusTrap } from '../lib/focusTrap.js'

/**
 * *Add to a list* — the button, the sheet it opens, and the line it leaves
 * under itself (Q19). Mounted once on each album, song and artist page, with
 * the target as props: `release_group`, `recording` or `artist`, the catalog's
 * words, never the client's "album" and "song".
 *
 * **The lists load when the sheet opens, not with the page.** Every open
 * reads `GET /me/lists` again, which says for each of your lists whether it
 * already holds this thing, so a list that does is marked *added* and is not
 * a button before anyone picks it. A page that fetched them on render would
 * spend a request on the most-visited page for a control most visits never
 * press.
 *
 * **The count still decides what the line says.** `contains` is true as of the
 * open; an add from another tab after that makes the next press a duplicate,
 * which the API answers exactly like a fresh add — 200 and the list. Only the
 * entry count moving tells the two apart, so an unmoved count reads
 * *Already in*, never *Added to*.
 *
 * The sheet is `RatingSheet`'s presentation, for the same reason
 * `ReviewComposer` is: an action about this record, on this record's page.
 */
export default function AddToList({ targetType, targetPublicId, subject, className = '' }) {
  const [open, setOpen] = useState(false)
  const [lists, setLists] = useState(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [status, setStatus] = useState(null)
  const newListButton = useRef(null)
  const formWasOpen = useRef(false)

  function close() {
    setOpen(false)
  }

  const dialogRef = useFocusTrap(open, close)

  // Another album, song or artist in the same page component: nothing said
  // about the last one is true of this one.
  useEffect(() => {
    setOpen(false)
    setStatus(null)
  }, [targetType, targetPublicId])

  useEffect(() => {
    if (!open) return undefined
    const controller = new AbortController()
    setLists(null)
    setLoadFailed(false)
    api
      .myLists(targetType, targetPublicId, { signal: controller.signal })
      .then(setLists)
      .catch((err) => {
        if (err.name !== 'AbortError') setLoadFailed(true)
      })
    return () => controller.abort()
  }, [open, attempt, targetType, targetPublicId])

  // Cancel hands focus back to *New list*, which is what opened the form.
  useEffect(() => {
    if (formOpen) formWasOpen.current = true
    else if (formWasOpen.current) newListButton.current?.focus()
  }, [formOpen])

  function openSheet() {
    setError('')
    setStatus(null)
    setFormOpen(false)
    formWasOpen.current = false
    setOpen(true)
  }

  function landed(updated, countBefore) {
    setStatus({
      kind: updated.item_count === countBefore ? 'already' : 'added',
      publicId: updated.public_id,
      title: updated.title,
    })
    close()
  }

  const target = { target_type: targetType, target_public_id: targetPublicId }

  async function add(list) {
    setBusy(true)
    setError('')
    try {
      landed(await api.addListItem(list.public_id, target), list.item_count)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that just now.')
    } finally {
      setBusy(false)
    }
  }

  // A refused create throws out of here into the form, which keeps what was
  // typed and shows the sentence. A create that lands and an add that does not
  // is this sheet's to say: the list exists now, so it becomes a row to retry.
  async function createAndAdd(body) {
    setBusy(true)
    setError('')
    try {
      const list = await api.createList(body)
      try {
        landed(await api.addListItem(list.public_id, target), list.item_count)
      } catch {
        setLists((current) => [{ ...list, contains: false }, ...(current || [])])
        formWasOpen.current = false
        setFormOpen(false)
        setError(`Made ${list.title}, but adding to it did not work. Pick it above to try again.`)
      }
    } finally {
      setBusy(false)
    }
  }

  const empty = lists !== null && lists.length === 0

  return (
    <div className={`add-to-list ${className}`.trim()}>
      <button type="button" className="btn btn-quiet" onClick={openSheet}>
        Add to a list
      </button>
      <p className="footnote mb-0" role="status">
        {status ? (
          <>
            {status.kind === 'added' ? 'Added to ' : 'Already in '}
            <Link to={`/lists/${status.publicId}`}>{status.title}</Link>.
          </>
        ) : null}
      </p>

      {open ? (
        <div className="sheet-backdrop" onClick={close} role="presentation">
          <div
            ref={dialogRef}
            className="sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Add to a list"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sheet-head">
              <div className="row-main">
                <h2 className="title-3 mb-0">Add to a list</h2>
                {subject ? <p className="footnote mb-0">{subject}</p> : null}
              </div>
              <SheetClose onClose={close} />
            </div>

            <div className="sheet-section">
              {loadFailed ? (
                <>
                  <InlineAlert>Your lists did not load.</InlineAlert>
                  <button
                    type="button"
                    className="btn btn-tonal btn-sm mt-2"
                    onClick={() => setAttempt((current) => current + 1)}
                  >
                    Try again
                  </button>
                </>
              ) : lists === null ? (
                <p className="footnote mb-0">Loading…</p>
              ) : empty ? (
                <p className="footnote mb-0">No lists yet</p>
              ) : (
                <ul className="list-index">
                  {lists.map((list) => (
                    <li key={list.public_id}>
                      {list.contains ? (
                        <div className="list-index-row">
                          <PickerRowText list={list} />
                          <span className="state-chip">added</span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="list-index-row picker-row"
                          onClick={() => add(list)}
                          disabled={busy}
                        >
                          <PickerRowText list={list} />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <InlineAlert>{error}</InlineAlert>
            </div>

            {lists !== null ? (
              <div className="sheet-section">
                {formOpen || empty ? (
                  <NewListForm
                    submitLabel="Create and add"
                    onSubmit={createAndAdd}
                    onCancel={empty ? undefined : () => setFormOpen(false)}
                    disabled={busy}
                  />
                ) : (
                  <button
                    ref={newListButton}
                    type="button"
                    className="btn btn-quiet btn-sm"
                    onClick={() => setFormOpen(true)}
                    disabled={busy}
                  >
                    New list
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

/** A list's title and the footnote the profile tab already uses for it. */
function PickerRowText({ list }) {
  return (
    <span className="row-main">
      <span className="rated-title">{list.title}</span>
      <span className="footnote">
        {list.item_count} {list.item_count === 1 ? 'entry' : 'entries'}
        {list.visibility === 'private' ? ' · private' : ''}
      </span>
    </span>
  )
}
